import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Tag, Table, Button, Space, Select, Input, Modal, Tooltip, message } from "antd";

import ReturnAddressModal from "./ReturnAddressModal";
import { isInternationalRecipient, recipientCountry } from "../../../modules/helpers/countries";

// Icons
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  SyncOutlined,
} from "@ant-design/icons";

// Moment
import moment from "moment";

// Firebase
import firebase from "firebase/compat/app";

import { newOpId, stamp } from "../../../modules/helpers/audit";

const STATUSES = (type) => ({
  prepare: {
    tag: (
      <Tag icon={<SyncOutlined />} color="processing">
        {type === "electronique" ? "En cours d'envoi" : "En préparation"}
      </Tag>
    ),
  },
  scheduled: {
    tag: (
      <Tag icon={<ClockCircleOutlined />} color="default">
        Programmé
      </Tag>
    ),
  },
  sent: {
    downloadText: "Preuve de dépôt",
    tag: (
      <Tag icon={<CheckCircleOutlined />} color="cyan">
        Envoyé
      </Tag>
    ),
  },
  accepted: {
    downloadText: "Preuve d'acceptation",
    tag: (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Accepté
      </Tag>
    ),
  },
  refused: {
    downloadText: "Preuve de refus",
    tag: (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Refusé
      </Tag>
    ),
  },
  neglected: {
    downloadText: "Preuve de négligence",
    tag: (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Négligé
      </Tag>
    ),
  },
  error_multiple: {
    tag: (
      <Tag icon={<WarningOutlined />} color="warning">
        Erreurs multiples
      </Tag>
    ),
  },
  error_address: {
    tag: (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Erreur d'adressage
      </Tag>
    ),
  },
  error_file: {
    tag: (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Erreur fichier
      </Tag>
    ),
  },
  error_print: {
    tag: (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Erreur d'impression
      </Tag>
    ),
  },
  error_print_param: {
    tag: (
      <Tag icon={<CloseCircleOutlined />} color="error">
        Erreur paramètres
      </Tag>
    ),
  },
});

export default function RecipientsTable({ data, recipients, messageType, companyId, messageId }) {
  const { type } = useParams();
  const resolvedType = messageType || type;
  let showTitle = false;
  let arr;

  if (recipients) {
    arr = recipients;
    showTitle = true;
  } else {
    arr = data;
  }

  const dispatch = useDispatch();
  const db = firebase.firestore();
  const [loadingId, setLoadingId] = useState(null);
  const [returnAddressRecord, setReturnAddressRecord] = useState(null);

  function openReturnAddress(record) {
    setReturnAddressRecord(record);
  }

  const [printingId, setPrintingId] = useState(null);
  // Destinataire France dont la saisie libre du n° de suivi est ouverte
  const [manualEntryKey, setManualEntryKey] = useState(null);

  const [reserveSingle] = useState(() =>
    firebase.app().functions("europe-west1").httpsCallable("reserveTrackingNumber")
  );
  const [releaseSingle] = useState(() =>
    firebase.app().functions("europe-west1").httpsCallable("releaseTrackingNumber")
  );
  const [generateLiasses] = useState(() =>
    firebase.app().functions("europe-west1").httpsCallable("generateARLiasses", { timeout: 300000 })
  );

  async function handleReserveSingle(record) {
    setLoadingId(record.key);
    try {
      await reserveSingle({ companyId, messageId, recipientId: record.key });
      message.success("Numéro de suivi attribué");
    } catch (error) {
      message.error(error.message || "Erreur lors de l'attribution");
    } finally {
      setLoadingId(null);
    }
  }

  async function handlePrintSingle(record) {
    setPrintingId(record.key);
    const win = window.open("", "_blank");
    try {
      const result = await generateLiasses({ companyId, messageId, recipientId: record.key });
      const { url, pdf } = result.data;
      // Prod : URL du bucket. Aperçu : PDF base64.
      let target = url;
      if (!target && pdf) {
        const bytes = atob(pdf);
        const buf = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
        target = URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
      }
      if (win) win.location = target;
      else window.location.href = target;
    } catch (error) {
      if (win) win.close();
      message.error(error.message || "Erreur lors de la génération de la liasse");
    } finally {
      setPrintingId(null);
    }
  }

  async function handleStatusChange(record, newStatus) {
    if (!companyId || !messageId) return;
    try {
      await db
        .collection("companies").doc(companyId)
        .collection("messages").doc(messageId)
        .collection("recipients").doc(record.key)
        .update({
          status: newStatus,
          send_date: newStatus === "sent" ? firebase.firestore.FieldValue.serverTimestamp() : null,
          last_updated: firebase.firestore.FieldValue.serverTimestamp(),
          last_change: stamp({ op: newOpId() }),
        });
      message.success("Statut mis à jour");
    } catch (error) {
      message.error("Erreur lors du changement de statut");
    }
  }

  async function handleRelease(record) {
    setLoadingId(record.key);
    try {
      await releaseSingle({ companyId, messageId, recipientId: record.key });
      message.success("Numéro libéré");
    } catch (error) {
      message.error(error.message || "Erreur lors de la libération");
    } finally {
      setLoadingId(null);
    }
  }

  // International : pays hors France d'après la liste de référence (« FR », « FRA »… sont la
  // France) ; un pays non reconnu (anciennes données) reste international et est signalé.
  const isIntl = isInternationalRecipient;

  async function saveRecipientFields(record, fields) {
    if (!companyId || !messageId) return false;
    try {
      await db
        .collection("companies").doc(companyId)
        .collection("messages").doc(messageId)
        .collection("recipients").doc(record.key)
        .update({
          ...fields,
          last_updated: firebase.firestore.FieldValue.serverTimestamp(),
          last_change: stamp(),
        });
      return true;
    } catch (error) {
      message.error("Erreur lors de la mise à jour");
      return false;
    }
  }

  function confirmManual(title, content) {
    return new Promise((resolve) => {
      Modal.confirm({
        title,
        content,
        okText: "Enregistrer quand même",
        cancelText: "Annuler",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  // Numéro de suivi saisi à la main (international, liasse pré-imprimée, écoulement de stock,
  // cas particulier) : pas de tracking_number_ref → jamais imprimé, juste conservé pour le suivi.
  // Normalisé sans espaces ni minuscules (« 2C 175 894 8923 2 » → « 2C17589489232 ») pour le lien
  // de suivi La Poste. Avertissements non bloquants : longueur inhabituelle, numéro appartenant
  // au stock des plages nationales (non réservé en saisie libre : il pourrait être réattribué).
  const POOL_STATUS_LABELS = { AVAILABLE: "disponible", RESERVED: "réservé", USED: "utilisé" };

  async function handleManualTracking(record, value) {
    const v = (value || "").replace(/\s+/g, "").toUpperCase();
    if (!v) return;
    if (v.length !== 13) {
      const ok = await confirmManual(
        "Format inhabituel",
        `${v.length} caractère(s) au lieu de 13. Enregistrer quand même ?`
      );
      if (!ok) return;
    }
    try {
      const pool = await db
        .collection("tracking_numbers")
        .where("raw_number", "==", v)
        .limit(1)
        .get();
      if (!pool.empty) {
        const label = POOL_STATUS_LABELS[pool.docs[0].data().status];
        const ok = await confirmManual(
          "Numéro du stock",
          `Ce numéro est dans le stock${label ? ` (${label})` : ""} : non réservé en saisie libre, ` +
            "il pourrait être réattribué. Enregistrer quand même ?"
        );
        if (!ok) return;
      }
    } catch (error) {
      // Contrôle d'aide seulement : une erreur de lecture du stock n'empêche pas la saisie
      console.error("Contrôle du stock des numéros impossible", error);
    }
    if (await saveRecipientFields(record, { trackingNumber: v, trackingType: "manual" })) {
      setManualEntryKey(null);
      message.success("Numéro de suivi enregistré");
    }
  }

  async function handleClearManual(record) {
    if (await saveRecipientFields(record, { trackingNumber: null, trackingType: null })) {
      message.success("Numéro effacé");
    }
  }

  function manualTrackingInput(record, placeholder, autoFocus = false) {
    return (
      <Input.Search
        size="small"
        style={{ width: 240 }}
        placeholder={placeholder}
        enterButton="Enregistrer"
        autoFocus={autoFocus}
        onSearch={(value) => handleManualTracking(record, value)}
      />
    );
  }

  const columnsHead = [
    { title: "Nom", dataIndex: "last_name" },
    { title: "Prénom", dataIndex: "first_name" },
    {
      title: "Type",
      dataIndex: "type",
      render: (t) => (t === "individual" ? "Particulier" : "Professionnel"),
    },
  ];

  if (resolvedType !== "recommande") {
    columnsHead.push({ title: "Email", dataIndex: "email" });
  }

  const addressColumn = {
    title: "Adresse",
    key: "address",
    render: (_, record) => (
      <>
        <div>{record.address_line_1}</div>
        {record.address_line_2 && <div>{record.address_line_2}</div>}
        <div>{record.postal_code} {record.city}</div>
        {isIntl(record) &&
          (recipientCountry(record) ? (
            <Tag color="orange" style={{ marginTop: 4 }}>{recipientCountry(record).name}</Tag>
          ) : (
            <Tooltip title="Pays absent de la liste de référence : à vérifier avec le client">
              <Tag color="red" style={{ marginTop: 4 }}>pays non reconnu : {record.country}</Tag>
            </Tooltip>
          ))}
      </>
    ),
  };

  const referenceColumn = {
    title: "Réf.",
    dataIndex: "reference",
    render: (ref) => ref || "—",
  };

  const returnAddressColumn = {
    title: "Adresse de retour",
    key: "return_address",
    render: (_, record) => {
      const hasAddress = record.return_address_line_1 || record.return_address_city;
      return (
        <Space direction="vertical" size={2}>
          {hasAddress ? (
            <>
              {record.return_address_name && <div>{record.return_address_name}</div>}
              <div>{record.return_address_line_1}</div>
              {record.return_address_line_2 && <div>{record.return_address_line_2}</div>}
              <div>{record.return_address_postal_code} {record.return_address_city}</div>
              <Button size="small" type="link" style={{ padding: 0 }} onClick={() => openReturnAddress(record)}>
                Changer
              </Button>
            </>
          ) : (
            <Button size="small" onClick={() => openReturnAddress(record)}>
              Renseigner
            </Button>
          )}
        </Space>
      );
    },
  };

  const trackingColumn = {
    title: "N° de suivi",
    key: "tracking",
    render: (_, record) => {
      if (loadingId === record.key) {
        return <Button size="small" loading>...</Button>;
      }
      if (record.trackingNumber) {
        const url = `https://www.laposte.fr/outils/suivre-vos-envois?code=${record.trackingNumber}`;
        const isManual = !record.tracking_number_ref; // international / liasse pré-imprimée
        return (
          <Space>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {record.trackingNumber}
            </a>
            {/* Saisie libre : aucune liasse imprimée par Solidoc pour ce destinataire */}
            {isManual && <Tag>saisie libre</Tag>}
            {isManual ? (
              record.status === "prepare" && (
                <Button size="small" danger onClick={() => handleClearManual(record)}>
                  Effacer
                </Button>
              )
            ) : (
              <>
                {record.status === "prepare" && (
                  <Button size="small" danger onClick={() => handleRelease(record)}>
                    Libérer
                  </Button>
                )}
                <Button
                  size="small"
                  loading={printingId === record.key}
                  onClick={() => handlePrintSingle(record)}
                >
                  Imprimer
                </Button>
              </>
            )}
          </Space>
        );
      }
      // International : pas de plage nationale → saisie manuelle du numéro.
      if (isIntl(record)) {
        return manualTrackingInput(record, "N° de suivi (saisie manuelle)");
      }
      // France : n° des plages nationales par défaut ; saisie libre possible (liasse
      // pré-imprimée, écoulement de stock, cas particulier), sans liasse imprimée.
      return (
        <Space direction="vertical" size={2}>
          <Button size="small" onClick={() => handleReserveSingle(record)}>
            Obtenir n°
          </Button>
          {manualEntryKey === record.key ? (
            <Space size={4}>
              {manualTrackingInput(record, "N° de suivi", true)}
              <Button type="link" size="small" onClick={() => setManualEntryKey(null)}>
                Annuler
              </Button>
            </Space>
          ) : (
            <Tooltip title="Liasse pré-imprimée ou cas particulier : pas de liasse imprimée">
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => setManualEntryKey(record.key)}
              >
                Saisir un n°
              </Button>
            </Tooltip>
          )}
        </Space>
      );
    },
  };

  const statusColumnReadonly = {
    title: "Dernier status",
    dataIndex: "status",
    render: (status) => STATUSES(resolvedType)[status] && STATUSES(resolvedType)[status].tag,
  };

  const statusColumnEditable = {
    title: "Statut",
    dataIndex: "status",
    width: 180,
    render: (status, record) => (
      <Select
        size="small"
        value={status}
        style={{ width: "100%" }}
        onChange={(value) => handleStatusChange(record, value)}
      >
        <Select.Option value="prepare">En préparation</Select.Option>
        <Select.Option value="sent">Envoyé</Select.Option>
        <Select.Option value="error_address">Erreur adressage</Select.Option>
        <Select.Option value="error_file">Erreur fichier</Select.Option>
        <Select.Option value="error_print">Erreur impression</Select.Option>
        <Select.Option value="error_print_param">Erreur paramètres</Select.Option>
      </Select>
    ),
  };

  const dateColumn = {
    title: "Date de status",
    dataIndex: "last_updated",
    render: (lastUpdated) =>
      lastUpdated && moment(lastUpdated.toDate()).format("DD/MM/YYYY HH:mm"),
  };

  const columns = [
    ...columnsHead,
    ...(resolvedType === "recommande"
      ? [addressColumn, referenceColumn, returnAddressColumn, trackingColumn, statusColumnEditable, dateColumn]
      : [statusColumnReadonly, dateColumn]),
  ];

  return (
    <>
    <ReturnAddressModal
      companyId={companyId}
      messageId={messageId}
      record={returnAddressRecord}
      onClose={() => setReturnAddressRecord(null)}
    />
    <Table
      title={showTitle ? () => <h4>Liste des destinataires</h4> : null}
      columns={columns}
      dataSource={arr}
      pagination={false}
      size="small"
      loading={!arr}
      locale={{ emptyText: "Récupération des données" }}
      bordered
      expandable={{
        expandedRowRender: proofsTable(
          () => {
            dispatch({ type: "SHOW_LOADER", payload: "Récupération de la preuve..." });
          },
          () => {
            dispatch({ type: "HIDE_LOADER" });
          }
        ),
        expandIcon: ({ expanded, onExpand, record }) => (
          <Button
            size="small"
            style={{ fontSize: 12, fontWeight: 600 }}
            onClick={(e) => onExpand(record, e)}
          >
            {resolvedType === "electronique" ? "Status et preuves" : "Status"}
          </Button>
        ),
      }}
    />
    </>
  );
}

function proofsTable(showLoader, hideLoader) {
  return function TableComponent(data) {
    const { type } = useParams();

    const columns = [
      {
        title: "Nom de status",
        dataIndex: "type",
        render: (status) => STATUSES(type)[status]?.tag,
      },
      {
        title: "Date",
        dataIndex: "date",
        render: (timestamp) => moment(timestamp.toDate()).format("DD/MM/YYYY à HH:mm"),
      },
    ];

    if (type === "electronique") {
      const linkUrl =
        process.env.REACT_APP_PROJECT_ID === "solidoc"
          ? "https://storage.googleapis.com/solidoc-proofs/"
          : "https://storage.googleapis.com/solidoc-proofs-dev/";
      columns.push({
        title: "Preuve",
        dataIndex: "path",
        render: (path, record) =>
          path ? (
            <a href={`${linkUrl}${path}`} target="_blank" rel="noopener noreferrer">
              {STATUSES(type)[record.type].downloadText}
            </a>
          ) : (
            ""
          ),
      });
    }

    const proofs = data.proofs
      ? data.proofs.map((proof) => ({
          ...proof,
          key: proof.date.toDate().getTime(),
        }))
      : [];

    return (
      <Table
        title={() => <h4>{type === "electronique" ? "Status et preuves" : "Status"}</h4>}
        columns={columns}
        dataSource={proofs}
        pagination={false}
        size="small"
      />
    );
  };
}
